import {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { getToken, isImpersonating } from "../utils/authToken";
import axiosInstance from "../api/axios";
import {
  clearFarmAuth,
  farmAuthFromApi,
  loadFarmAuth,
  mergeFarmAuth,
  saveFarmAuth,
} from "../utils/farmAuthStorage";

export let UserContext = createContext();

async function fetchProfileFarmAuth() {
  const res = await axiosInstance.get("/auth/profile");
  const profile = res.data?.data?.profile;
  return farmAuthFromApi(profile);
}

export default function UserContextProvider(props) {
  const [Authorization, setAuthorization] = useState(null);
  const [farmAuth, setFarmAuthState] = useState(() => loadFarmAuth());
  const hadAuthRef = useRef(false);

  const setFarmAuth = useCallback((next) => {
    if (!next) {
      clearFarmAuth();
      setFarmAuthState(null);
      return;
    }
    saveFarmAuth(next);
    setFarmAuthState(next);
  }, []);

  const hydrateFarmAuth = useCallback((source) => {
    const next = farmAuthFromApi(source);
    if (next) setFarmAuth(next);
    return next;
  }, [setFarmAuth]);

  const refreshFarmAuthFromProfile = useCallback(async () => {
    const token = getToken();
    if (!token) return null;
    try {
      const next = await fetchProfileFarmAuth();
      if (next) setFarmAuth(next);
      return next;
    } catch {
      return farmAuth;
    }
  }, [farmAuth, setFarmAuth]);

  // Initialize token + farm auth from storage; refresh profile when needed
  useEffect(() => {
    const token = getToken();
    if (token) {
      setAuthorization(token);
      const impersonating = isImpersonating();
      const stored = loadFarmAuth();
      // Don't reuse stored farm auth while impersonating — it may belong to the admin
      if (stored && !impersonating) setFarmAuthState(stored);

      const needsProfile =
        impersonating ||
        !stored?.enabledAnimalTypes?.length ||
        stored.enabledAnimalTypes.length === 0;

      if (needsProfile) {
        refreshFarmAuthFromProfile();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear farm auth only when user logs out (not on initial mount)
  useEffect(() => {
    if (Authorization) {
      hadAuthRef.current = true;
      return;
    }
    if (hadAuthRef.current) {
      clearFarmAuth();
      setFarmAuthState(null);
      hadAuthRef.current = false;
    }
  }, [Authorization]);

  const contextValue = useMemo(
    () => ({
      Authorization,
      setAuthorization,
      farmAuth,
      setFarmAuth,
      hydrateFarmAuth,
      refreshFarmAuthFromProfile,
      mergeFarmAuth: (partial) => {
        const next = mergeFarmAuth(partial);
        setFarmAuthState(next);
        return next;
      },
    }),
    [
      Authorization,
      farmAuth,
      hydrateFarmAuth,
      refreshFarmAuthFromProfile,
      setFarmAuth,
    ]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {props.children}
    </UserContext.Provider>
  );
}
